import { CaretLeft, Error, Lock, Warning } from '@clearkit/icons';
import classNames from 'classnames';
import {
  CKBannerNext,
  CKBox,
  CKButton,
  CKCheckboxNext,
  CKLayoutGrid,
  CKSkeleton,
} from 'clearkit';
import { capitalize } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '~/components/app/appMainRoutes';
import StripeCCInput, {
  Ref as StripeCCInputRef,
} from '~/components/billing/StripeCCInput';
import { CreditSelector } from '~/components/CreditSelector';
import { GrowthPlan } from '~/components/PlanColumns';
import { useCurrentUser } from '~/components/profile/useCurrentUser';
import { useStatusToast } from '~/components/unified/hooks';
import { PageLayout } from '~/components/unified/PageLayout';
import {
  AccountFlagEnum,
  AccountPlanEnum,
  BillingInfo,
  useAccountBillingInfoQuery,
  useAccountUpdateBillingInfoMutation,
  useUpgradeAccountPlanMutation,
} from '~/generated/graphql';
import { useFeatureFlag } from '~/hooks/account';
import { useCreditUsage } from '~/hooks/account/useCreditUsage';
import { creditOptions } from '~/lib/selfServePlanHelpers';
import { usePublishableKeys } from '~/pages/Batches/hooks/usePublishableKeys';

export const LoadingUpgradeSkeleton = () => {
  return (
    <div className="p-4">
      <CKSkeleton className="w-1/3 mb-4 rounded h-[2rem]" isLoading />

      <CKSkeleton className="w-1/4 mb-4 rounded h-[2.5rem]" isLoading />

      <div className="mb-4">
        <CKSkeleton className="mb-2 rounded h-[2rem]" isLoading />
        <CKSkeleton className="mb-2 rounded h-[2rem]" isLoading />
        <CKSkeleton className="rounded h-[2rem]" isLoading />
      </div>

      <CKSkeleton className="w-3/4 mb-4 rounded h-[2.5rem]" isLoading />

      <CKSkeleton className="w-1/2 mb-4 rounded h-[1.5rem]" isLoading />

      <CKSkeleton className="w-1/4 rounded h-[3rem]" isLoading />
    </div>
  );
};

const GrowthUpgrade = ({
  account,
  refetchAccount,
}: {
  account: any;
  refetchAccount: () => void;
}) => {
  const {
    isHubspotAccount,
    planId: currentPlanId,
    credits,
    isPaidAccount,
  } = useCreditUsage();

  const defaultCreditGrant = '1000';
  const { publishableKeys } = usePublishableKeys();
  const stripeCCInputRef = useRef<StripeCCInputRef>(null);
  const [ccErrorMessage, setCCErrorMessage] = useState<string | null>(null);
  const [tosError, setTosError] = useState(false);
  const [isEditingCC, setIsEditingCC] = useState(false);
  const [tosChecked, setTosChecked] = useState(false);
  const [creditGrantValue, setCreditGrantValue] = useState<string>(
    defaultCreditGrant,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showInvoiceFailedBanner = useFeatureFlag(
    AccountFlagEnum.LastInvoiceFailed,
  ).enabled;

  const lastInvoiceSucceeded = useFeatureFlag(
    AccountFlagEnum.LastInvoiceSucceeded,
  ).enabled;

  const { data: billingData, refetch } = useAccountBillingInfoQuery();

  const [accountBilling, setAccountBillingInfo] = useState<
    BillingInfo | undefined
  >(billingData?.accountBillingInfo);

  useEffect(() => {
    setAccountBillingInfo(billingData?.accountBillingInfo);
    setIsEditingCC(!billingData?.accountBillingInfo.cardLastFour);
  }, [billingData]);

  useEffect(() => {
    if (currentPlanId && credits && isPaidAccount) {
      setCreditGrantValue(String(credits));
    }
  }, [currentPlanId, credits]);

  const { addSuccessToast } = useStatusToast();

  const [upgradeAccountPlan] = useUpgradeAccountPlanMutation({
    onCompleted: () => {
      setIsSubmitting(false);
      refetchAccount();
      addSuccessToast({
        description:
          'Your subscription limit will be updated when your invoice has been successfully processed. You will recieve an email confirmation shortly.',
        heading: 'Subscription Updated',
      });
    },
  });

  const upgradeToGrowth = async () => {
    return await upgradeAccountPlan({
      variables: {
        input: {
          planId: isHubspotAccount
            ? AccountPlanEnum.HubspotPaidTier
            : AccountPlanEnum.PaidTier,
          creditGrant: Number(creditGrantValue),
        },
      },
    });
  };

  const [updateBillingInfo] = useAccountUpdateBillingInfoMutation();

  const onSubmit = async () => {
    if (!tosChecked) {
      setTosError(true);
      return;
    }

    setIsSubmitting(true);

    const { stripe, card } = stripeCCInputRef.current || {};

    // Do not submit upgrade if there is no card saved
    if (stripe && card && isEditingCC) {
      stripe?.createToken(card).then(async (result) => {
        if (result.error) {
          setCCErrorMessage(result?.error?.message as string);
          setIsSubmitting(false);
        } else {
          setCCErrorMessage(null);
          await updateBillingInfo({
            variables: {
              input: {
                cardToken: result.token.id,
              },
            },
          });

          await upgradeToGrowth();
          refetch();
        }
      });
    } else if (accountBilling?.cardLastFour) {
      upgradeToGrowth();
    }
  };

  const selectedCreditOptions = creditOptions(isHubspotAccount);

  const isLoading = !publishableKeys || !publishableKeys.STRIPE_PUBLISHABLE_KEY;
  const isDirty = credits !== Number(creditGrantValue);

  const userFacingPlanName = isPaidAccount ? 'growth' : 'free';

  return (
    <PageLayout>
      <PageLayout.Header>
        <PageLayout.Heading>Plans & Billing</PageLayout.Heading>
        <PageLayout.SubHeading>
          {isLoading ? (
            'Loading...'
          ) : (
            <>
              Your team — <strong>{account.name}</strong> — is currently on the{' '}
              {capitalize(userFacingPlanName)} plan
              {isPaidAccount ? ` with ${credits} credits per month` : ''}.
            </>
          )}
        </PageLayout.SubHeading>
      </PageLayout.Header>
      <PageLayout.Body>
        {showInvoiceFailedBanner ? (
          <CKBannerNext isFullWidth variant="error">
            <CKBannerNext.IconStatus>
              <Error />
            </CKBannerNext.IconStatus>
            <CKBannerNext.Body>
              An error occurred when processing your payment. Please re-enter
              your card information and try again.
            </CKBannerNext.Body>
          </CKBannerNext>
        ) : null}

        {!showInvoiceFailedBanner && !lastInvoiceSucceeded && isPaidAccount ? (
          <CKBannerNext isFullWidth variant="warning">
            <CKBannerNext.IconStatus>
              <Warning />
            </CKBannerNext.IconStatus>
            <CKBannerNext.Body>
              Invoice has not yet been processed. You will recieve email
              confirmation shortly after processing and your credit limit will
              be increased.
            </CKBannerNext.Body>
          </CKBannerNext>
        ) : null}
        {isLoading ? (
          <LoadingUpgradeSkeleton />
        ) : (
          <div className="px-10 pt-8">
            <CKLayoutGrid gap="2rem" gridTemplateColumns="16rem 1fr">
              <GrowthPlan />
              <div className="space-y-8">
                <div className="pb-8 border-b border-gray-100">
                  <CreditSelector
                    onChange={(value) => setCreditGrantValue(value)}
                    options={selectedCreditOptions}
                    value={creditGrantValue}
                  />
                </div>

                <div>
                  <span className="block w-full mb-4 text-base font-medium text-gray-600">
                    Enter your payment information
                  </span>
                  <CKBox className="px-6 py-4">
                    {ccErrorMessage && isEditingCC && (
                      <span className="mb-2 text-base font-medium text-red-500">
                        {ccErrorMessage}
                      </span>
                    )}
                    {accountBilling?.cardLastFour && !isEditingCC ? (
                      <div className="flex flex-row items-center justify-between mb-4 text-base text-gray-700">
                        <div className="flex flex-row items-center">
                          <span
                            className={classNames(
                              'p-2 border border-gray-200 rounded mr-2',
                            )}
                          >
                            ************{accountBilling.cardLastFour}
                          </span>
                          <span>{accountBilling.billingEmail}</span>
                        </div>
                        <CKButton
                          onClick={() => setIsEditingCC(true)}
                          variant="tertiary"
                          variantColor="blue"
                        >
                          Update
                        </CKButton>
                      </div>
                    ) : (
                      <StripeCCInput
                        className={classNames(
                          'p-2 border border-gray-200 rounded mb-4',
                        )}
                        ref={stripeCCInputRef}
                        stripePublishableKey={
                          publishableKeys.STRIPE_PUBLISHABLE_KEY
                        }
                      />
                    )}
                    <div className="flex flex-row items-center text-base text-gray-700">
                      <Lock className="mr-2" />
                      <span>Payments securely processed by Stripe</span>
                    </div>
                  </CKBox>
                </div>

                <div className={'pb-24 text-base'}>
                  <span
                    className={classNames(
                      'block w-full mb-4 font-medium text-gray-600',
                      { 'text-red-500': tosError },
                    )}
                  >
                    Read and accept the Clearbit terms & conditions
                  </span>
                  <CKBox className="px-6 py-4">
                    <CKCheckboxNext
                      onChange={(checked) => {
                        setTosError(false);
                        setTosChecked(checked);
                      }}
                    >
                      I have read and agree to the{' '}
                      <a
                        href="https://clearbit.com/legal"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        Clearbit TOS
                      </a>{' '}
                      and{' '}
                      <a
                        href="https://clearbit.com/privacy-policy"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        privacy policy
                      </a>
                    </CKCheckboxNext>
                  </CKBox>
                </div>
              </div>
            </CKLayoutGrid>
          </div>
        )}
      </PageLayout.Body>
      <PageLayout.Footer className="flex items-center justify-between bg-white">
        <CKButton
          as={Link}
          leftIcon={<CaretLeft />}
          to={routes.plans}
          variant="tertiary"
          variantColor="blue"
        >
          Back to plans
        </CKButton>
        <div className="flex flex-row items-center">
          {isDirty ? (
            <span className="max-w-md text-sm text-gray-500 text-wrap">
              You’re upgrading to the Clearbit Growth plan with{' '}
              {creditGrantValue} credits for $
              {
                creditOptions(isHubspotAccount).find(
                  (option) => option.value === creditGrantValue,
                )?.cost
              }{' '}
              per month. Credits refreshed monthly. Cancel anytime.
            </span>
          ) : null}
          <CKButton
            isDisabled={isSubmitting || (isPaidAccount && !isDirty)}
            isLoading={isSubmitting}
            onClick={onSubmit}
            variant="bold"
            variantColor="green"
          >
            {isSubmitting ? 'Processing...' : 'Upgrade Plan'}
          </CKButton>
        </div>
      </PageLayout.Footer>
    </PageLayout>
  );
};

export const PageTeamGrowthUpgrade = () => {
  const {
    account,
    loading: accountLoading,
    refetch: refetchAccount,
  } = useCurrentUser();

  if (accountLoading || !account) {
    return <LoadingUpgradeSkeleton />;
  }

  return <GrowthUpgrade account={account} refetchAccount={refetchAccount} />;
};
